import {http, HttpResponse} from 'msw';
import {SetupServerApi} from 'msw/node';

import {
  CashRegisterResponseBody,
  TaxDocumentCalculationV2ResponseBody,
  VatTypeEnum,
} from '@dms/api/shared';

const getCashRegisterResponseMock: CashRegisterResponseBody = {
  id: 'test-cash-register-id',
  name: 'Main Cash Register',
  code: 'TCR01',
  currency: 'CZK',
  expenseSeriesId: 'expense-series-id',
  incomeSeriesId: 'income-series-id',
  branchId: 'branch-id',
  activeFrom: '2024-01-01',
  eligibleForCardPayments: true,
  cashRegisterAccount: '123456789',
  currentBalance: {
    amount: '1000.00',
    currency: 'CZK',
  },
  initialBalance: {
    amount: '0.00',
    currency: 'CZK',
  },
  active: true,
  system: false,
  eetCode: null,
  closedAt: null,
  documentCount: 5,
  createdBy: 'user-id',
  createdAt: '2024-01-01T00:00:00Z',
};

const getTaxDocumentCalculationV2ResponseMock: TaxDocumentCalculationV2ResponseBody = {
  calculation: {
    taxableSupplyDate: '2024-01-01',
    vatTypes: [VatTypeEnum.S],
    vatTypesSummary: {
      S: {
        groupSummary: {
          priceWithVat: {
            amount: '121.00',
            currency: 'CZK',
          },
          priceWithoutVat: {
            amount: '100.00',
            currency: 'CZK',
          },
          vat: {
            amount: '21.00',
            currency: 'CZK',
          },
        },
        additionalCalculation: null,
        vatRate: '21',
      },
    },
    priceWithVat: {
      amount: '121.00',
      currency: 'CZK',
    },
    priceWithoutVat: {
      amount: '100.00',
      currency: 'CZK',
    },
    priceWithVatBeforeRounding: {
      amount: '121.00',
      currency: 'CZK',
    },
    vat: {
      amount: '21.00',
      currency: 'CZK',
    },
    items: [],
    summaryItems: [],
    usedRoundingDefinitionAndValidity: {
      definition: {
        id: 'def-1',
        branchId: null,
        paymentType: 'cash',
        validities: [],
        isSystem: true,
      },
      validity: {
        validFrom: '2024-01-01',
        roundType: 'ALGEBRAIC',
        roundStep: '1',
        taxRounding: false,
      },
    },
  },
  exchangeRateRatioCalculation: {
    taxableSupplyDate: '2024-01-01',
    vatTypes: [VatTypeEnum.S],
    vatTypesSummary: {
      S: {
        groupSummary: {
          priceWithVat: {
            amount: '121.00',
            currency: 'CZK',
          },
          priceWithoutVat: {
            amount: '100.00',
            currency: 'CZK',
          },
          vat: {
            amount: '21.00',
            currency: 'CZK',
          },
        },
        additionalCalculation: null,
        vatRate: '21',
      },
    },
    priceWithVat: {
      amount: '121.00',
      currency: 'CZK',
    },
    priceWithoutVat: {
      amount: '100.00',
      currency: 'CZK',
    },
    priceWithVatBeforeRounding: {
      amount: '121.00',
      currency: 'CZK',
    },
    vat: {
      amount: '21.00',
      currency: 'CZK',
    },
    items: [],
    summaryItems: [],
    usedRoundingDefinitionAndValidity: {
      definition: {
        id: 'def-1',
        branchId: null,
        paymentType: 'cash',
        validities: [],
        isSystem: true,
      },
      validity: {
        validFrom: '2024-01-01',
        roundType: 'ALGEBRAIC',
        roundStep: '1',
        taxRounding: false,
      },
    },
  },
};

const postInvoicingDocumentRecalculateResponseMock = {
  items: [
    {
      itemId: 'item-1',
      relatedItemId: null,
      quantity: '1',
      unitPrice: '100.00',
      unit: 'ks',
      unitType: 'standard' as const,
      priceWithVat: '121.00',
      priceWithoutVat: '100.00',
      isUnitPriceWithVat: false,
      vatType: VatTypeEnum.S,
      type: 'standard' as const,
      description: 'Test Item',
      currency: 'CZK',
      discount: {
        relatedItemId: 'item-1',
        percent: '0',
        originalPrice: '100.00',
        priceAfterDiscount: '100.00',
        discountValue: '0.00',
      },
    },
  ],
  documentSummary: {
    calculation: getTaxDocumentCalculationV2ResponseMock.calculation,
    exchangeRateRatioCalculation:
      getTaxDocumentCalculationV2ResponseMock.exchangeRateRatioCalculation,
  },
};

const getCashRegisterResponseInterceptor = (server: SetupServerApi) =>
  server.use(
    http.get('*/dms/v1/cash-registers/*', () => HttpResponse.json(getCashRegisterResponseMock))
  );

const postTaxDocumentCalculationV2ResponseInterceptor = (server: SetupServerApi) =>
  server.use(
    http.post('*/v2/tax-document-calculation', () =>
      HttpResponse.json(getTaxDocumentCalculationV2ResponseMock)
    )
  );

const postInvoicingDocumentRecalculateResponseInterceptor = (server: SetupServerApi) =>
  server.use(
    http.post('*/dms/v2/invoicing-document-items/recalculate', () =>
      HttpResponse.json(postInvoicingDocumentRecalculateResponseMock)
    )
  );

export const accountingApiMock = {
  getCashRegisterResponseMock,
  getCashRegisterResponseInterceptor,
  getTaxDocumentCalculationV2ResponseMock,
  postTaxDocumentCalculationV2ResponseInterceptor,
  postInvoicingDocumentRecalculateResponseMock,
  postInvoicingDocumentRecalculateResponseInterceptor,
};
